import React, {useContext, useEffect, useState} from "react";
import {Modal, ScrollView, View} from "react-native";
import MainStyle from "../../MainStyle";
import Div from "../lib/Div";
import P from "../lib/P";
import FontAwesome5 from "@expo/vector-icons/FontAwesome5";
import Btn from "../components/Btn";
import {StoreContext} from "../components/StoreContextProvider";
import AmountSelect from "../components/AmountSelect";
import {FullSwingInfoModalProps, ShotAmountOption} from "../types";
import Switch from "../components/Switch";
import {thumbStyle, trackStyle} from "../constants";
import FontAwesome from '@expo/vector-icons/FontAwesome';
import FullSwingInfoModal from "./FullSwingInfoModal";

const emptyInfoModal: FullSwingInfoModalProps = {visible: false, title: '', subtitle: '', errorIcon: false};

type Props = {
  visible?: boolean, onRequestClose: () => void, amount: number,
  onConfirm: (isMax: boolean, amount: number, singleShotMode: boolean) => Promise<void> | void,
  isFreePlay: boolean, isMax: boolean
};

const ShotAmountModal = (props: Props) => {
  const {visible, isFreePlay, amount, isMax} = props;
  const [items, setItems] = useState<ShotAmountOption[]>([]);
  const Context = useContext(StoreContext);
  const {extendedUserData} = Context;
  const [value, setValue] = useState(0);
  const [maxValue, setMaxValue] = useState(false);
  // SMG-3051: Single Shot mode is the per-shot CONFIRM setting (bay-session
  // on_shot), independent of engine autoPlayEnabled. Sourced from
  // Context.confirmationBypassEnabled (mirrors on_shot via the PlayerState
  // projection: on_shot=true -> bypass=false -> single-shot ON). Reading
  // !autoPlayEnabled here was the persistence bug — every modal-open
  // re-initialized the toggle from the engine flag (which is now decoupled
  // and stays put), so toggling Single Shot ON then confirming the modal a
  // second time silently wrote on_shot=false back.
  const singleShotMode = !Context.confirmationBypassEnabled;
  const [draftSingleShotMode, setDraftSingleShotMode] = useState(singleShotMode);
  const [infoModal, setInfoModal] = useState<FullSwingInfoModalProps>(emptyInfoModal);

  const resetInfoModal = () => {
    setInfoModal(emptyInfoModal);
  };

  const onSubmit = async () => {
    await props.onConfirm(maxValue, value, draftSingleShotMode);
  };

  const amountChange = (val: number) => () => {
    setValue(val);
    setMaxValue(false);
  };

  const handleMax = () => {
    setMaxValue(!maxValue);
    if (!maxValue) setValue(extendedUserData.maxShotAmount);
  };

  useEffect(() => {
    const val: ShotAmountOption[] = [];
    const shotOptions = [3, 5, 10, 15, 20];

    shotOptions.forEach(shotOption => {
      if (shotOption * 100 <= extendedUserData.maxShotAmount) {
        val.push({value: shotOption * 100, label: `$${shotOption}`, disabled: false});

      } else {
        val.push({value: shotOption * 100, label: `$${shotOption}`, disabled: true});
      }
    });

    setItems(val);
  }, [extendedUserData.maxShotAmount]);

  // This modal is a DRAFT surface: the three controls below are staged locally
  // and committed together by onSubmit. So each one is seeded from props when
  // the modal OPENS and is never re-seeded while it is open — a prop that moves
  // mid-edit must not overwrite a choice the player has already made but not yet
  // confirmed. Depend on `visible` alone; adding the seeded value to the dep
  // array is what reintroduces the stomp.
  //
  // Reported symptom: disable Always Play Max, enable Single Shot, then cancel
  // the bet confirmation, and Always Play Max switches itself back on. The max
  // effect was the only one of the three with no `visible` guard at all, so ANY
  // isMax transition reached it. Cancelling refreshes `user` — directly via
  // Context.getUser() on the legacy path, via a PlayerState frame on the
  // bay-session path — which re-runs FullSwingHomePage's isMax effect. That
  // effect reads a DIFFERENT field per branch (`user.autoPlayEnabled ?
  // autoPlayAmount === -2 : lastBetAmount === -2`), so when autoPlayEnabled
  // changes across the refresh, isMax flips branches and the switch snaps back.
  //
  // Latent since 2026-01-13 (max) / 2025-12-16 (the isMax recompute). It stayed
  // invisible while auto_play_enabled was pinned TRUE by session enrollment:
  // the branch never switched, so isMax never transitioned. SMG-4387 made that
  // flag genuinely volatile (durable consent + the
  // applyUserSnapshot-vs-mergeStreamUserProjection race) and woke it up. The
  // other two controls are given the same treatment because they share the
  // defect in weaker form — both re-seed whenever their source prop moves, and
  // Single Shot sits directly in the reported repro.
  useEffect(() => {
    if (!visible) return;
    setValue(amount);
    setMaxValue(isMax);
    setDraftSingleShotMode(singleShotMode);
  }, [visible]);

  return (
    <>
      <Modal animationType="fade" transparent visible={visible} statusBarTranslucent={false} hardwareAccelerated={false}
             onRequestClose={props.onRequestClose}>
        <View style={[MainStyle.fix_gradient_and_modal_error_in_landscape, MainStyle.flex1, MainStyle.bg_filter_modal]}>
          <ScrollView contentContainerStyle={MainStyle.flex1}>
            <Div minH="100%" justifyContent="flex-end">
              <Div bg="#131314" w="100%" rounded={16} overflow="hidden">
                <Div position="relative" bg="#252526" justifyContent="center">
                  <P fontFamily="DavisSans-Medium" ta="center" lh={48}>Shot Amount</P>
                  <FontAwesome5
                    name="times" size={28} color="white" style={[MainStyle.absolute, MainStyle.r_10]}
                    onPress={props.onRequestClose}
                  />
                </Div>

                <Div px={20} pb={20}>
                  <P fontFamily="DavisSans-Regular" ta="center" my={30}>Keep playing to unlock higher amounts!</P>

                  <Div w={200} mx="auto" roundedTop={4} overflow="hidden" mb={30}>
                    {items.map(el => (
                      <AmountSelect key={String(el.value)} testID={`shot-amount-option-${el.value}`}
                                    selected={el.value === value} disabled={el.disabled}
                                    onPress={amountChange(el.value)}>
                        {el.label}
                      </AmountSelect>
                    ))}
                  </Div>

                  <Div row w={295} justifyContent="space-between" mx="auto" alignItems="center" py={8}>
                    <P fontFamily="DavisSans-Regular" onPress={handleMax}>Always play max amount</P>
                    <Switch value={maxValue} trackStyle={trackStyle} thumbStyle={thumbStyle} onPress={handleMax}/>
                  </Div>

                  <Div w={295} mx="auto" h={1} bg="#252526" mb={8}/>

                  <Div row w={295} mx="auto" alignItems="center" py={8}>
                    <P fontFamily="DavisSans-Regular"
                       onPress={() => setDraftSingleShotMode(!draftSingleShotMode)}>
                      Single Shot mode
                    </P>
                    <FontAwesome
                      name="question-circle-o" size={18} color="#BBBCBF" style={MainStyle.m_l_8}
                      onPress={() => setInfoModal({
                        visible: true, title: 'Single Shot Mode', showCloseIcon: true,
                        subtitle: 'Play one shot at a time at your own pace. Practice as much as you like, then confirm on your phone when you\'re ready for an official shot.',
                        secondSubtitle: 'If turned off, each shot will be confirmed automatically.',
                        noteText: '(Previously called Auto Play.)'
                      })}
                    />

                    <Div flex={1}/>

                    <Switch
                      value={draftSingleShotMode} thumbStyle={thumbStyle} trackStyle={trackStyle}
                      onPress={() => setDraftSingleShotMode(!draftSingleShotMode)}
                    />
                  </Div>

                  <Div w={295} mx="auto" h={1} bg="#252526" mb={30}/>

                  <Btn testID="shot-amount-confirm" bg="#3369FF" onPress={onSubmit}>
                    <P fontFamily="DavisSans-Medium" lh={56} ta="center">{isFreePlay ? 'Got it!' : 'Confirm'}</P>
                  </Btn>
                </Div>
              </Div>
            </Div>
          </ScrollView>
        </View>
      </Modal>

      <FullSwingInfoModal info={infoModal} onRequestClose={resetInfoModal}/>
    </>
  );
};

export default ShotAmountModal;
